const INVOKE_MUTATION = `mutation InvokeOceanAgent($input: InvokeOceanAgentInput!) {
  invokeOceanAgent(input: $input) { jobId status }
}`;
const JOB_QUERY = `query GetAIJob($jobId: ID!) {
  getAIJob(jobId: $jobId) {
    jobId status errorMessage providerUsageJson outputAssetKey costCents
  }
}`;
async function gqlFetch(url, jwt, appId, body) {
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            "X-Vida-AppId": appId,
        },
        body: JSON.stringify(body),
    });
    const json = (await res.json());
    if (json.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
    }
    return json.data;
}
function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}
function parseOutputText(providerUsageJson) {
    if (!providerUsageJson)
        return null;
    try {
        const u = JSON.parse(providerUsageJson);
        return u.outputText?.trim() || null;
    }
    catch {
        return null;
    }
}
/** Async Q/A: invokeOceanAgent + poll getAIJob until terminal state. */
export async function askAgent(opts) {
    const data = await gqlFetch(opts.appsyncUrl, opts.jwt, opts.appId, {
        query: INVOKE_MUTATION,
        variables: {
            input: {
                agentId: opts.agentId,
                orgId: opts.orgId,
                goal: opts.goal,
                userQuestion: opts.userQuestion?.trim() || undefined,
                contextJson: opts.contextJson != null ? JSON.stringify(opts.contextJson) : undefined,
            },
        },
    });
    const job = data?.invokeOceanAgent;
    if (!job?.jobId)
        throw new Error("invokeOceanAgent: missing jobId");
    const interval = opts.pollIntervalMs ?? 2000;
    const timeout = opts.pollTimeoutMs ?? 900_000;
    const started = Date.now();
    while (Date.now() - started < timeout) {
        await sleep(interval);
        const poll = await gqlFetch(opts.appsyncUrl, opts.jwt, opts.appId, {
            query: JOB_QUERY,
            variables: { jobId: job.jobId },
        });
        const row = poll?.getAIJob;
        if (!row)
            continue;
        if (row.status === "COMPLETED" || row.status === "FAILED" || row.status === "CANCELLED") {
            return {
                jobId: job.jobId,
                status: row.status,
                outputText: parseOutputText(row.providerUsageJson),
                costCents: row.costCents,
                errorMessage: row.errorMessage,
            };
        }
    }
    throw new Error("askAgent: poll timeout");
}
/** Parse SSE lines from ocean-agent-stream Lambda Function URL. */
export async function* streamAgent(opts) {
    const fetchImpl = opts.fetchFn ?? fetch;
    const res = await fetchImpl(opts.streamUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            // SigV4 uses Authorization for AWS credentials — pass Cognito JWT separately.
            "x-oidc-token": opts.jwt,
            "X-Vida-AppId": opts.appId,
        },
        body: JSON.stringify({
            agentId: opts.agentId,
            goal: opts.goal,
            orgId: opts.orgId,
            userQuestion: opts.userQuestion,
            contextJson: opts.contextJson,
        }),
    });
    if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        throw new Error(`streamAgent HTTP ${res.status}: ${text.slice(0, 200)}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const yieldSseParts = function* (parts) {
        for (const part of parts) {
            const trimmed = part.trim();
            if (!trimmed)
                continue;
            const line = trimmed.split("\n").find((l) => l.startsWith("data: "));
            if (!line)
                continue;
            try {
                yield JSON.parse(line.slice(6));
            }
            catch {
                /* skip malformed chunk */
            }
        }
    };
    while (true) {
        const { done, value } = await reader.read();
        if (value) {
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n\n");
            buffer = parts.pop() ?? "";
            yield* yieldSseParts(parts);
        }
        if (done) {
            buffer += decoder.decode();
            const tail = buffer.split("\n\n").filter((p) => p.trim());
            buffer = "";
            yield* yieldSseParts(tail);
            break;
        }
    }
}
